const base = require('../product/base');
const { getCookie, getDataFromDispatcherCookie } = require('../common/utils/cookieUtils');
const { default: getFrontConfig } = require('../common/getFrontConfig');
const { onOnce } = require('../common/utils/domEvents');
const isMatchedBreakpoint = require('../common/utils/isMatchedBreakpoint');
const { getVariationGroupId } = require('../common/helpers/productIdHelpers');
const { analyticsEventCenter } = require('./events');

const FRONT_CONFIG = getFrontConfig();

/**
 * Given a product ID, return the product container element.
 * @param {string} productId - The product ID.
 * @returns {HTMLElement|null} The product container element.
 */
function getProductContainer(productId) {
  return document.querySelector(`.product-detail-vue[data-pid^="${productId}"]`);
}

/**
 * Get the hidden SFRA size selector for a product, or the first one on the page if no product ID is provided.
 * @param {HTMLElement|null} container - The product container element
 * @returns {HTMLElement|null} The size selector element
 */
function getSizeSelector(container) {
  return container?.querySelector('._product-detail-data_ .select-size');
}

/**
 * The initPDP function is called when the productDetailsViewed event is fired. This event is fired when
 * the product details page is loaded
 */
function init() {
  analyticsEventCenter.subscribe(
    analyticsEventCenter.EVENTS.productDetailsViewed,
    initPDP,
  );
  document.addEventListener('order:success', initSuccess);

  onOnce(window, 'beforeunload', () => {
    analyticsEventCenter.unsubscribe(
      analyticsEventCenter.EVENTS.productDetailsViewed,
      initPDP,
    );
    document.removeEventListener('order:success', initSuccess);
  });
}

/**
 * It sets the data for PDP fitAnalyticsData object
 * @param {Object} data - Event data object
 * @param {FullProduct} data.product - The product
 */
function initPDP(data) {
  if (data?.product) {
    setFitAnalyticsData();
    setMainImageLink(data.product);
    setCurrentItemSubgroupId(data.product);
    setAllItemSubgroupIds(data.product);
    setSizes(data.product);
    setOperations();
    loadScript('PDP');
  }
}

/**
 * It the data for Order Confirm fitAnalyticsData object
 * @param {CustomEvent} e - The event object that is passed to the function.
 */
function initSuccess(e) {
  const { orderId, products } = e.detail;
  if (orderId && products) {
    setFitAnalyticsData();
    setOrderId(orderId);
    setCurrency();
    setProducts(products);
    loadScript('OCP');
  }
}

/**
 * It creates a new script element, sets the source to the script type passed in, and appends it to the
 * head of the document
 * @param {string} type - The type of script to load. This can be either PDP or OCP.
 */
function loadScript(type) {
  const scriptSrc = FRONT_CONFIG?.fitAnalyticsScripts[type];
  const script = document.querySelector(`script[src="${scriptSrc}"]`);
  if (script || !scriptSrc) return;
  const fitAnalyticsScript = document.createElement('script');
  fitAnalyticsScript.setAttribute('async', '');
  fitAnalyticsScript.setAttribute('src', scriptSrc);
  document.head.appendChild(fitAnalyticsScript);
}

/**
 * It takes a product ID and size, and then adds the product to the cart
 * @param {string} productId - The product variation group ID
 * @param {string} size - The size of the product
 */
function operationAddToCart(productId, size) {
  const pid = productId + size;
  const addToCartBtn = operationGetAddToCartBtn();

  const addToCartData = {
    pid,
    addToCartPlace: 'Fit Analytics',
  };

  const form = new URLSearchParams();
  form.append('pid', pid);
  form.append('quantity', 1);
  form.append('removeSaveForLater', !addToCartBtn?.dataset.isLimited);

  base.actionAddToCart(form, addToCartData);
}

/**
 * If the screen is mobile, return the mobile add to cart button, otherwise return the desktop add to
 * cart button.
 * @returns {HTMLElement|null} The add to cart button.
 */
function operationGetAddToCartBtn() {
  const isBreakpointMobile = isMatchedBreakpoint.matches();
  const selectorModiffier = isBreakpointMobile ? 'mobile' : 'desktop';
  return document.querySelector(`.the-product-details__add-to-cart-btn--${selectorModiffier}`);
}

/**
 * It returns the current selected size of the product
 * @returns {string} The selected size of the product.
 */
function operationGetCurrentSize() {
  const productId = window.fitAnalyticsData?.currentItemSubgroupId;
  const productContainer = getProductContainer(productId);
  const sizeSelector = getSizeSelector(productContainer);
  if (sizeSelector) {
    const options = Array.from(sizeSelector.options);
    const sizeOption = options?.find(
      (option) => option.value === sizeSelector.value && option.dataset.attrValue,
    );
    return sizeOption ? sizeOption.dataset.attrValue : '';
  }
  return '';
}

/**
 * Returns the element that opens the size guide modal
 * @returns {HTMLElement|null} The size chart button
 */
function operationGetSizeChartLink() {
  return document.querySelector('.the-product-details__size-guide-btn');
}

/**
 * It takes a product ID and size, and selects the given size of given product ID if available
 * @param {string} productId - The product variation group ID
 * @param {string} size - The size of the product
 */
function operationSelectSize(productId, size) {
  const productContainer = getProductContainer(productId);
  const sizeSelector = getSizeSelector(productContainer);
  if (sizeSelector) {
    const options = Array.from(sizeSelector.options);
    const sizeOption = options?.find((option) => option.dataset.attrValue === size);
    if (
      sizeOption
      && !sizeOption?.disabled
      && sizeOption?.value !== sizeSelector.value
    ) {
      sizeSelector.value = sizeOption.value;

      fetch(sizeOption.value, {
        method: 'GET',
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.error) {
            // eslint-disable-next-line no-console
            console.warn('Message:', data.message);
          } else {
            $('body').trigger(
              'product:afterAttributeSelect',
              { data, container: $(productContainer) },
            );
          }
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error:', error);
        });
    }
  }
}

/**
 * It sets the currentItemSubgroupId property of the window.fitAnalyticsData object
 * to an array of all color variation ids of the product passed in as an argument
 * @param {dw.order.ProductLineItem} product - The product object
 */
function setAllItemSubgroupIds(product) {
  const colorVariationAttr = product.variationAttributes?.find((variationAttribute) => variationAttribute.id === 'color');
  const allItemSubgroupIds = colorVariationAttr?.values.map((value) => `${product.upid}.${value.value}`);
  window.fitAnalyticsData.allItemSubgroupIds = allItemSubgroupIds;
}

/**
 * It sets the currency property of the window.fitAnalyticsData object to the
 * defaultCurrency property of FRONT_CONFIG
 */
function setCurrency() {
  window.fitAnalyticsData.currency = FRONT_CONFIG?.defaultCurrency;
}

/**
 * It sets the currentItemSubgroupId property of the window.fitAnalyticsData object
 * to the id of the product (variation group) passed in as an argument
 * @param {dw.order.ProductLineItem} product - The product object
 */
function setCurrentItemSubgroupId(product) {
  window.fitAnalyticsData.currentItemSubgroupId = getVariationGroupId(product.id);
}

/**
 * It sets the shopLanguage, shopCountry, userId, and shopSessionId properties
 * of the window.fitAnalyticsData object
 */
function setFitAnalyticsData() {
  window.fitAnalyticsData = {};
  setLocale();
  setUserId();
  setShopSessionId();
}

/**
 * It sets the shopLanguage and shopCountry properties of the window.fitAnalyticsData object
 */
function setLocale() {
  const {
    lang = FRONT_CONFIG?.currentLanguageCode,
    country = FRONT_CONFIG?.countryCode,
  } = getDataFromDispatcherCookie();
  window.fitAnalyticsData.shopLanguage = lang.toLowerCase();
  window.fitAnalyticsData.shopCountry = country.toUpperCase();
}

/**
 * It sets the mainImageLink property of the window.fitAnalyticsData object
 * @param {dw.order.ProductLineItem} product - The product object
 */
function setMainImageLink(product) {
  const imageType = FRONT_CONFIG?.productImages.imageTypes.PDP_480;
  const url = product.bylImages && product.bylImages[imageType]?.[0].url;
  window.fitAnalyticsData.mainImageLink = url;
}

/**
 * It sets the operations property of the window.fitAnalyticsData object to an object containing functions
 * that will be used by the Fit Analytics script to interact with the page
 */
function setOperations() {
  window.fitAnalyticsData.operations = {
    getAddToCartBtn: operationGetAddToCartBtn,
    getSizeChartLink: operationGetSizeChartLink,
    addToCart: operationAddToCart,
    selectSize: operationSelectSize,
    getCurrentSize: operationGetCurrentSize,
  };
}

/**
 * It sets the orderId property of the window.fitAnalyticsData object to the orderId parameter
 * @param {string} orderId - The order ID of the order that was just placed.
 */
function setOrderId(orderId) {
  window.fitAnalyticsData.orderId = orderId;
}

/**
 * It takes the products from the order:success event, maps them into the format that Fit Analytics expects
 * and sets the product property of the window.fitAnalyticsData object to the mapped products
 * @param {dw.order.ProductLineItem[]} products - An array of products.
 */
function setProducts(products) {
  const fitAnalyticsProducts = products.map((product) => {
    const size = product.variationAttributes?.find((variationAttribute) => variationAttribute.id === 'size');
    return {
      size: size?.value,
      id: product.id,
      itemSubgroupId: getVariationGroupId(product.id),
      price: parseFloat(product?.price?.sales?.decimalPrice).toFixed(2),
      quantity: product.quantity?.toString(),
      gtin: product.EAN,
    };
  });
  window.fitAnalyticsData.products = fitAnalyticsProducts;
}

/**
 * It sets the shopSessionId property of the window.fitAnalyticsData object
 * with the value of the sid cookie exists
 */
function setShopSessionId() {
  const sidCookieValue = getCookie('sid');
  let sid;
  if (sidCookieValue.length > 0) {
    sid = sidCookieValue.replaceAll('"', '');
  }
  window.fitAnalyticsData.shopSessionId = sid;
}

/**
 * It sets the sizes property of the window.fitAnalyticsData object
 * to an array of all sizes {value, isAvailable} of the product passed in as an argument
 * @param {dw.order.ProductLineItem} product - The product object
 */
function setSizes(product) {
  const sizeVariationAttr = product.variationAttributes?.find((variationAttribute) => variationAttribute.id === 'size');
  const sizes = sizeVariationAttr?.values.map((value) => (
    { value: value.value, isAvailable: value.selectable }
  ));
  window.fitAnalyticsData.sizes = sizes;
}

/**
 * It sets the userId property of the window.fitAnalyticsData object
 * to the user_id property of the gaInfo object
 */
function setUserId() {
  window.fitAnalyticsData.userId = gaInfo?.fitAnalyticsUUID;
}

module.exports = {
  init,
};
